import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, GeneralDialog, Select } from "@ptsd/uikit";
import i18next from "i18next";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Dialog, Form, View, XStack } from "tamagui";
import { z } from "zod";

import { api } from "~/utils/api";
import { InviteStatusTable } from "./invite-status-table";

const formSchema = z.object({
  groupId: z
    .string()
    .min(2, {
      message: i18next.t("group:validation-errors.min-length"),
    })
    .max(50, {
      message: i18next.t("group:validation-errors.max-length"),
    }),
});

type MoveToAnotherGroupForm = z.infer<typeof formSchema>;

export const MoveToAnotherGroup: React.FC<{
  resetSelectedRows: () => void;
  trigger: React.ReactNode;
  userIds: string[];
  userEmails?: (string | null)[];
  groupId: string;
}> = ({ resetSelectedRows, userIds, userEmails, trigger, groupId }) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation("group");

  const { control, reset, handleSubmit } = useForm<MoveToAnotherGroupForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      groupId: "",
    },
  });

  const utils = api.useUtils();

  const { data: groups, isLoading: isGroupsLoading } =
    api.group.getGroupNamesWithId.useQuery(undefined, {
      select: (data) => {
        const defalutValue = data.find((group) => group.id === groupId);
        return {
          defaultValue: defalutValue?.id ?? "",
          options: data.map((group) => ({
            value: group.id,
            name: group.name ?? "",
          })),
        };
      },
    });

  const { mutate, isLoading } = api.person.moveToAnotherGroup.useMutation({
    onSuccess: () => {
      void utils.person.list.invalidate();
      resetSelectedRows();
    },
  });

  const onSubmit = (values: MoveToAnotherGroupForm) => {
    mutate({ groupId: values.groupId, userIds: userIds });
    reset();
    setOpen((prev) => !prev);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Dialog.Trigger data-id="move-to-another-group-dialog-trigger" asChild>
        {trigger}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay
          zIndex={0}
          key={"move-to-another-group-dialog-overlay"}
          style={{
            backgroundColor: "#6A696966",
            backdropFilter: "blur(10px)",
          }}
        />
        <GeneralDialog.Content
          data-id="move-to-another-group-dialog-content"
          maxWidth={600}
          width="100%"
        >
          <Form onSubmit={handleSubmit(onSubmit)}>
            <View
              width="100%"
              gap={"$5"}
              alignItems="center"
              paddingTop={"$5"}
              paddingBottom={"$4"}
            >
              <View gap={"$2"}>
                <GeneralDialog.Title data-id="move-to-another-group-dialog-title">
                  {t("move-to-another-group.title")}
                </GeneralDialog.Title>

                <InviteStatusTable
                  data={{
                    message: t("move-to-another-group.message"),
                    details: userEmails?.map((email) => ({ email })) ?? [],
                    columns: [{ key: "email", name: "Користувач" }],
                  }}
                />
              </View>

              <Controller
                control={control}
                name="groupId"
                render={({ field }) => (
                  <XStack width="100%" gap={"$2"} justifyContent="center">
                    <Select
                      maxWidth={400}
                      trigger={
                        isGroupsLoading
                          ? "Завантаження"
                          : t("move-to-another-group.placeholder")
                      }
                      label={t("move-to-another-group.placeholder")}
                      defaultValue={groups?.defaultValue}
                      items={groups?.options ?? []}
                      onValueChange={(val) => field.onChange(val)}
                    />
                  </XStack>
                )}
              />
            </View>

            <GeneralDialog.ControllArea
              gap={"$2"}
              alignItems="center"
              paddingVertical={"$3"}
            >
              <Form.Trigger asChild>
                <Button
                  data-id="move-to-another-group-dialog-submit"
                  variant="primary"
                  minWidth={180}
                  maxWidth={320}
                  width={"100%"}
                  size="md"
                  rounded="lg"
                >
                  {isLoading
                    ? t("move-to-another-group.submit.loading")
                    : t("move-to-another-group.submit.text")}
                </Button>
              </Form.Trigger>

              <GeneralDialog.Close data-id="move-to-another-group-dialog-close">
                {t("move-to-another-group.close")}
              </GeneralDialog.Close>
            </GeneralDialog.ControllArea>
          </Form>
        </GeneralDialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};
